import type { UnifiedSceneState } from '@/types/unifiedSceneState';

export interface ParsedPrompt {
  character: string;
  environment: string;
  camera: string;
  lighting: string;
  style: string;
  mood: string;
  palette: string;
}

export interface PromptConflicts {
  repeatedTokens: string[];
  unresolvedPlaceholders: string[];
  excessiveDuplicates: boolean;
}

const uniqueParts = (parts: string[]) => {
  const seen = new Set<string>();
  const out: string[] = [];
  parts.forEach((part) => {
    const normalized = part.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push(part.trim());
  });
  return out;
};

const findFirst = (text: string, patterns: RegExp[]): string => {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].trim();
    if (match?.[0]) return match[0].trim();
  }
  return '';
};

export function parsePromptToScene(prompt: string): ParsedPrompt {
  const text = prompt.trim();

  const character = findFirst(text, [
    /\b(queen|king|princess|prince|warrior|detective|soldier|boy|girl|woman|man|cat|dog)\b/i,
    /(?:character|subject|hero)\s*:\s*([^,\.\n]+)/i,
  ]);

  const environment = findFirst(text, [
    /(?:in|on|at)\s+(a\s+)?(balcony|forest|street|city|castle|beach|desert|room|studio|park|mountain|palace)\b/i,
    /(?:environment|location|scene)\s*:\s*([^,\.\n]+)/i,
  ]).replace(/^(a\s+)?/i, '');

  const lighting = findFirst(text, [
    /\b(sunset|sunrise|golden hour|moonlight|cinematic lighting|soft lighting|dramatic lighting|low key|high key|neon lighting)\b/i,
    /(?:lighting|light)\s*:\s*([^,\.\n]+)/i,
  ]);

  const camera = findFirst(text, [
    /\b(close-up|wide shot|medium shot|top-down|low angle|eye-level|overhead|35mm|50mm|85mm)\b/i,
    /(?:camera|angle|lens|shot)\s*:\s*([^,\.\n]+)/i,
  ]);

  const style = findFirst(text, [
    /\b(cinematic|photorealistic|anime|noir|vintage|documentary|editorial)\b/i,
    /(?:style|look)\s*:\s*([^,\.\n]+)/i,
  ]);

  const mood = findFirst(text, [
    /\b(dramatic|moody|dark|happy|melancholic|epic|romantic|mysterious)\b/i,
    /(?:mood|tone|atmosphere)\s*:\s*([^,\.\n]+)/i,
  ]);

  const palette = findFirst(text, [
    /\b(teal and orange|warm tones|cool tones|monochrome|pastel|neon palette|golden palette)\b/i,
    /(?:palette|color palette|colors?)\s*:\s*([^,\.\n]+)/i,
  ]);

  return {
    character,
    environment,
    camera,
    lighting,
    style,
    mood,
    palette,
  };
}

const PLACEHOLDER_TOKENS = new Set([
  'subject', 'environment', 'lighting', 'camera', 'style', 'mood', 'palette', 'character', 'composition',
]);

const normalizeSpaces = (value: string) => value.replace(/\s+/g, ' ').trim();

const isUnresolvedPlaceholder = (value: string): boolean => {
  const normalized = normalizeSpaces(value).toLowerCase();
  if (!normalized) return true;
  if (PLACEHOLDER_TOKENS.has(normalized)) return true;
  const tokens = normalized.split(' ');
  return tokens.length > 0 && tokens.every((token) => PLACEHOLDER_TOKENS.has(token));
};

const resolveValue = (value: string, fallback: string): string => {
  const normalized = normalizeSpaces(value);
  if (isUnresolvedPlaceholder(normalized)) return fallback;
  return normalized;
};

export function cleanPrompt(text: string): string {
  return text
    .replace(/\b(\w+)(\s+\1\b)+/gi, '$1')
    .replace(/(,\s*){2,}/g, ', ')
    .replace(/\s+,/g, ',')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizePrompt(prompt: string): string {
  let normalized = cleanPrompt(prompt);

  // Remove duplicate prefix patterns (any "X shot/scene/render of" repeated)
  normalized = normalized.replace(/^(.+?\b(?:shot|scene|render|painting|illustration|image|panel|composition|depiction|diorama|aesthetic)\s+of\s+)\1+/i, '$1');

  normalized = cleanPrompt(normalized);
  return normalized;
}

export function detectPromptConflicts(text: string): PromptConflicts {
  const tokenMatches: string[] = text.toLowerCase().match(/\b[a-z]+\b/g) || [];
  const repeatedTokens: string[] = Array.from(new Set(
    tokenMatches.filter((token: string, index: number) => tokenMatches.indexOf(token) !== index),
  ));

  const unresolvedPlaceholders = Array.from(
    new Set(tokenMatches.filter((token) => PLACEHOLDER_TOKENS.has(token))),
  );

  const excessiveDuplicates = /(\b\w+\b)(\s+\1\b){2,}/i.test(text)
    || /(?:cinematic shot of\s*){2,}/i.test(text);

  return { repeatedTokens, unresolvedPlaceholders, excessiveDuplicates };
}

export function repairPrompt(text: string): string {
  let repaired = normalizePrompt(text);
  PLACEHOLDER_TOKENS.forEach((placeholder) => {
    repaired = repaired.replace(new RegExp(`\\b${placeholder}\\b`, 'gi'), '');
  });
  repaired = normalizePrompt(repaired);
  return repaired || 'Shot of main subject, scene environment, medium shot, natural lighting, dramatic mood, ultra-detailed.';
}

export function composePrompt(basePrompt: string, sceneState: UnifiedSceneState): string {
  const parsed = parsePromptToScene(basePrompt);

  // Build subject from character reference if available
  let subjectText = sceneState.subject || parsed.character || 'main subject';
  if (sceneState.character?.faceDescription) {
    const charParts = [
      sceneState.character.faceDescription,
      sceneState.character.hair ? `${sceneState.character.hair} hair` : '',
      sceneState.character.clothing || '',
      sceneState.character.pose || '',
    ].filter(Boolean);
    subjectText = `a person with ${charParts.join(', ')}`;
  }

  const subject = resolveValue(subjectText, 'main subject');
  const environment = resolveValue(sceneState.environment || parsed.environment, 'scene environment');
  const camera = resolveValue(sceneState.camera || parsed.camera, 'medium shot');
  const lighting = resolveValue(sceneState.lighting || parsed.lighting, 'natural lighting');

  // Use style from reference if available
  const styleSource = sceneState.references?.styleReference || sceneState.style || parsed.style || '';
  const style = resolveValue(styleSource, '');
  const mood = resolveValue(sceneState.mood || parsed.mood, 'dramatic mood');

  const orderedSegments = uniqueParts([
    subject,
    environment,
    camera,
    lighting,
    style,
    mood,
  ].filter(Boolean));

  const composition = normalizeSpaces(sceneState.composition);
  const palette = resolveValue(sceneState.palette || parsed.palette, '');

  const suffix = uniqueParts([
    composition,
    palette ? `palette ${palette}` : '',
    'ultra-detailed',
  ]);

  // Use style-aware prefix instead of hardcoded "Cinematic shot of"
  const prefix = getStylePrefix(sceneState.style || parsed.style || '');

  return normalizePrompt(`${prefix}${orderedSegments.join(', ')}${suffix.length ? `, ${suffix.join(', ')}` : ''}.`);
}

function getStylePrefix(styleId: string): string {
  const prefixMap: Record<string, string> = {
    cinematic: 'Cinematic shot of ',
    noir: 'Film noir scene of ',
    neo_noir: 'Neo-noir scene of ',
    moody_noir: 'Moody noir shot of ',
    photorealistic: 'Photorealistic image of ',
    photoreal_hdr: 'HDR photograph of ',
    hyperreal: 'Hyperrealistic render of ',
    studio_portrait: 'Studio portrait of ',
    editorial: 'Editorial fashion shot of ',
    anime: 'Anime illustration of ',
    graphic_novel: 'Graphic novel panel of ',
    watercolor: 'Watercolor painting of ',
    oil_painting: 'Oil painting of ',
    painterly_oil: 'Oil painting of ',
    pencil_sketch: 'Pencil sketch of ',
    surrealism: 'Surrealist depiction of ',
    pop_art: 'Pop art rendering of ',
    impressionist: 'Impressionist painting of ',
    abstract: 'Abstract composition of ',
    dark_fantasy: 'Dark fantasy scene of ',
    mythic_epic: 'Mythic epic scene of ',
    fantasy: 'High fantasy scene of ',
    gothic_horror: 'Gothic horror scene of ',
    neon_cyberpunk: 'Cyberpunk neon scene of ',
    cyberpunk_ultra: 'Ultra cyberpunk scene of ',
    sci_fi: 'Sci-fi scene of ',
    post_apocalyptic: 'Post-apocalyptic scene of ',
    steampunk: 'Steampunk scene of ',
    vaporwave: 'Vaporwave aesthetic of ',
    pixel_art: 'Pixel art of ',
    low_poly: 'Low-poly 3D render of ',
    isometric: 'Isometric diorama of ',
    claymation: 'Claymation scene of ',
    art_deco: 'Art Deco composition of ',
    minimalist: 'Minimalist composition of ',
    comic_book: 'Comic book panel of ',
    vintage_film: 'Vintage film shot of ',
    aaa_game: 'AAA game cinematic of ',
    unreal_engine: 'Unreal Engine render of ',
    arri_alexa: 'ARRI ALEXA cinematic shot of ',
    kodak_vision3: 'Kodak Vision3 film shot of ',
    octane_render: 'Octane render of ',
    blender_cycles: 'Blender Cycles render of ',
  };
  return prefixMap[styleId] || 'Shot of ';
}

export function buildCinematicPrompt(sceneState: UnifiedSceneState): string {
  const composed = composePrompt(sceneState.initialPrompt, sceneState);
  const conflicts = detectPromptConflicts(composed);

  if (conflicts.unresolvedPlaceholders.length || conflicts.excessiveDuplicates) {
    return repairPrompt(composed);
  }

  return composed;
}
